/**
 * StockItemFormDialog - Cadastro/edição de rastreador ou chip
 *
 * Validação definitiva (IMEI/ICCID, situação, modelo/plano do tenant) nas RPCs
 * `upsert_stock_tracker` e `upsert_stock_chip`.
 *
 * RELEASE: 2026-10-16 v1.4.0 - Mostra o Endereço de Estoque (Padrão) e bloqueia sem padrão
 *          (mesmo padrão do kit/BR-EST-026; o destino da criação é o mesmo do servidor).
 * RELEASE: 2026-10-08 v1.3.0 - Rastreador: Fornecedor (+), ESN, Modelo com busca (+), sem Nº de série;
 *          Situação só na criação (BR-EST-022). Chip: Plano com busca. Atalhos "+" só com permissão de criar.
 * RELEASE: 2026-10-08 v1.2.0 - Atalho "+" para cadastrar plano de dados (com permissão).
 * RELEASE: 2026-10-07 v1.1.0 - Chip: campo Plano de dados (substitui Operadora) e
 *          datas de Ativação/Cancelamento somente leitura.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 3 do Estoque).
 */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { StockDataPlanFormDialog } from '@/components/stock/StockDataPlanFormDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { manualStatusesFor, useStockItemMutations, type StockItemRow } from '@/hooks/useStockItems';
import { dataPlanCarrierLabel, stockLocationTypeLabel, useDefaultStockLocation, useStockDataPlans, useTrackerModels } from '@/hooks/useStockRegistries';
import { formatDateOnlyBR } from '@/lib/utils/date-only';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { TrackerModelFormDialog } from '@/components/stock/TrackerModelFormDialog';

const NONE = '__none__';
const onlyDigits = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max);

interface Props {
  kind: 'tracker' | 'chip';
  open: boolean;
  item: StockItemRow | null;
  onOpenChange: (open: boolean) => void;
}

export function StockItemFormDialog({ kind, open, item, onOpenChange }: Props) {
  const [f, setF] = useState<Record<string, string>>({});
  const { upsertItem } = useStockItemMutations();
  const { canCreate } = usePermissions();
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showModelForm, setShowModelForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  // Rótulos de itens recém-criados pelo atalho (podem não estar na lista carregada)
  const [labels, setLabels] = useState<Record<string, string>>({});
  const isEdit = !!item;
  // Endereço de estoque padrão: destino automático da criação (mesma regra do kit/BR-EST-026)
  const { data: defaultLoc, isLoading: loadingLoc } = useDefaultStockLocation(open);
  // Modelos ativos para o seletor (só rastreador)
  const { data: models } = useTrackerModels({ search: '', status: 'active', page: 1, pageSize: 100 });
  // Planos ativos para o seletor (só chip)
  const { data: plans } = useStockDataPlans({ search: '', status: 'active', page: 1, pageSize: 100 }, open && kind === 'chip');

  useEffect(() => {
    if (!open) return;
    setF({
      imei: item?.imei ?? '', esn: item?.esn ?? '', supplier_person_id: item?.supplier_person_id ?? NONE, tracker_model_id: item?.tracker_model_id ?? NONE,
      iccid: item?.iccid ?? '', line_number: item?.line_number ?? '', imsi: item?.imsi ?? '', data_plan_id: item?.data_plan_id ?? NONE,
      status: item?.status ?? 'active', notes: item?.notes ?? '',
    });
    setLabels({ supplier: item?.supplier_name ?? '', model: item?.model_name ?? '', plan: item?.data_plan_name ?? '' });
  }, [open, item]);

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  // Plano vinculado inativo continua visível na edição
  const planOptions = [...(plans?.items ?? [])];
  if (item?.data_plan_id && item.data_plan_name && !planOptions.some((p) => p.id === item.data_plan_id)) {
    planOptions.push({ id: item.data_plan_id, name: `${item.data_plan_name} (inativo)`, compatible_carriers: [] } as never);
  }

  const save = () => {
    if (kind === 'tracker' && (f.imei.length < 14 || f.imei.length > 17)) {
      toast.error('IMEI precisa ter de 14 a 17 dígitos');
      return;
    }
    if (kind === 'chip' && (f.iccid.length < 18 || f.iccid.length > 22)) {
      toast.error('ICCID precisa ter de 18 a 22 dígitos');
      return;
    }
    // Plano de dados obrigatório para salvar/criar chip
    if (kind === 'chip' && (!f.data_plan_id || f.data_plan_id === NONE)) {
      toast.error('Selecione o plano de dados do chip');
      return;
    }
    const data = kind === 'tracker'
      ? { imei: f.imei, esn: f.esn, supplier_person_id: f.supplier_person_id === NONE ? null : f.supplier_person_id, tracker_model_id: f.tracker_model_id === NONE ? null : f.tracker_model_id, status: f.status, notes: f.notes }
      : { iccid: f.iccid, line_number: f.line_number, imsi: f.imsi, data_plan_id: f.data_plan_id === NONE ? null : f.data_plan_id, status: f.status, notes: f.notes };
    upsertItem.mutate({ kind, id: item?.id ?? null, data: { ...data, is_active: item?.is_active ?? true } }, { onSuccess: () => onOpenChange(false) });
  };

  const title = kind === 'tracker' ? (item ? 'Editar rastreador' : 'Novo rastreador') : (item ? 'Editar chip' : 'Novo chip');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {kind === 'tracker' ? (
            <>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>IMEI *</Label>
                <Input value={f.imei ?? ''} inputMode="numeric" onChange={(e) => set('imei', onlyDigits(e.target.value, 17))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>ESN</Label>
                <Input value={f.esn ?? ''} maxLength={30} placeholder="Opcional, complementar ao IMEI"
                  onChange={(e) => set('esn', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 30))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Modelo</Label>
                <div className="flex gap-2">
                  <SearchableSelect value={f.tracker_model_id === NONE ? null : f.tracker_model_id ?? null}
                    onChange={(v) => set('tracker_model_id', v ?? NONE)} noneLabel="Sem modelo" selectedLabel={labels.model}
                    options={(models?.items ?? []).map((m) => ({ value: m.id, label: m.name, hint: m.manufacturer_name }))} />
                  {/* Atalho só para quem pode criar modelos (validado também na RPC) */}
                  {canCreate('stock_models') && (
                    <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setShowModelForm(true)}
                      aria-label="Atalho: cadastrar novo modelo" title="Atalho: cadastrar novo modelo">
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Fornecedor</Label>
                <div className="flex gap-2">
                  <PersonSelector value={f.supplier_person_id === NONE ? null : f.supplier_person_id ?? null} role="supplier"
                    placeholder="Sem fornecedor" noneLabel="Sem fornecedor" selectedLabel={labels.supplier || null}
                    onSelect={(id) => set('supplier_person_id', id ?? NONE)} />
                  {canCreate('persons_suppliers') && (
                    <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setShowSupplierForm(true)}
                      aria-label="Atalho: cadastrar novo fornecedor" title="Atalho: cadastrar novo fornecedor">
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>ICCID *</Label>
                <Input value={f.iccid ?? ''} inputMode="numeric" onChange={(e) => set('iccid', onlyDigits(e.target.value, 22))} />
              </div>
              <div className="space-y-1.5">
                <Label>Número da linha</Label>
                <Input value={f.line_number ?? ''} inputMode="numeric" onChange={(e) => set('line_number', onlyDigits(e.target.value, 15))} />
              </div>
              <div className="space-y-1.5">
                <Label>IMSI</Label>
                <Input value={f.imsi ?? ''} inputMode="numeric" onChange={(e) => set('imsi', onlyDigits(e.target.value, 15))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Plano de dados *</Label>
                <div className="flex gap-2">
                <SearchableSelect value={f.data_plan_id === NONE ? null : f.data_plan_id ?? null}
                  onChange={(v) => set('data_plan_id', v ?? NONE)} noneLabel="Sem plano" selectedLabel={labels.plan}
                  options={planOptions.map((p) => ({ value: p.id, label: p.name, hint: p.compatible_carriers?.length ? p.compatible_carriers.map(dataPlanCarrierLabel).join(', ') : null }))} />
                {canCreate('stock_data_plans') && (
                  <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setShowPlanForm(true)}
                    aria-label="Atalho: cadastrar novo plano de dados" title="Atalho: cadastrar novo plano de dados">
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
                </div>
              </div>
              {/* Datas preenchidas automaticamente no futuro; somente leitura aqui */}
              <div className="space-y-1.5">
                <Label>Data de Ativação</Label>
                <Input disabled readOnly value={item?.activated_at ? formatDateOnlyBR(item.activated_at) : ''} placeholder="dd/mm/aaaa" />
              </div>
              <div className="space-y-1.5">
                <Label>Data de Cancelamento</Label>
                <Input disabled readOnly value={item?.cancelled_at ? formatDateOnlyBR(item.cancelled_at) : ''} placeholder="dd/mm/aaaa" />
              </div>
            </>
          )}
          {/* Bloco informativo do destino automático da criação (mesmo padrão do kit) */}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Endereço de Estoque (Padrão)</Label>
            {defaultLoc ? (
              <div className="rounded-md border bg-muted/40 p-3">
                <p className="font-medium">{defaultLoc.name}</p>
                <p className="text-sm text-muted-foreground">
                  {[stockLocationTypeLabel(defaultLoc.location_type), [defaultLoc.city, defaultLoc.state].filter(Boolean).join('/')].filter(Boolean).join(' - ')}
                </p>
              </div>
            ) : (
              <p className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">
                {loadingLoc ? 'Carregando endereço padrão...' : 'Nenhum endereço de estoque padrão. Marque um em Endereços de Estoque para cadastrar itens.'}
              </p>
            )}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Situação</Label>
            {/* BR-EST-022: rastreador só define a situação na criação (homologação futura altera) */}
            <Select value={f.status ?? 'active'} onValueChange={(v) => set('status', v)} disabled={isEdit && kind === 'tracker'}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {manualStatusesFor(kind).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isEdit && kind === 'tracker' && <p className="text-xs text-muted-foreground">A situação será alterada pela homologação.</p>}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Observações</Label>
            <Textarea value={f.notes ?? ''} maxLength={1000} rows={3} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {/* Sem endereço padrão não há destino válido: servidor já bloqueia; aqui é feedback imediato */}
          <Button onClick={save} disabled={upsertItem.isPending || !defaultLoc}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
      {/* Atalho: plano criado já fica selecionado no chip */}
      <StockDataPlanFormDialog open={showPlanForm} plan={null} onOpenChange={setShowPlanForm}
        onSuccess={(id) => set('data_plan_id', id)} />
      {/* Atalhos do rastreador: item criado já fica selecionado */}
      <TrackerModelFormDialog open={showModelForm} model={null} onOpenChange={setShowModelForm}
        onSuccess={(id, name) => { set('tracker_model_id', id); setLabels((l) => ({ ...l, model: name })); }} />
      <PersonFormDialog open={showSupplierForm} onOpenChange={setShowSupplierForm} person={null} defaultRole="supplier"
        onOpenExisting={() => undefined}
        onSuccess={(id, label) => { set('supplier_person_id', id); setLabels((l) => ({ ...l, supplier: label })); setShowSupplierForm(false); }} />
    </Dialog>
  );
}
